// Phase 16: Bank Reconciliation - a manual, per-voucher "tick as cleared by
// the bank" workflow. Deliberately a NEW, separate page from the Phase 9
// statement-import/auto-match workspace (BankReconciliationWorkspacePage.jsx,
// left completely untouched - see accountingNav.js for where it now lives).
// This page needs no bank-statement file at all: it lists every posted
// Receipt/Payment/Contra transaction against one real Bank Account (the
// same rows Bank Book already shows for that ledger) and lets the user mark
// each one Reconciled/Unreconciled with a Bank Cleared Date and an optional
// Bank Reference. Per item 5 of the spec this never creates, modifies or
// deletes a ledger entry - only the three reconciliation fields on a
// voucher's own bank-side line change; the accounting entry itself, and
// Bank Book's own balance, are completely unaffected (see the backend
// section this calls, search "PHASE 16" in server.py).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { toast } from "sonner";
import { SectionCard, EmptyState, DetailField, Field } from "./shared";
import SearchableSelect from "./SearchableSelect";
import { ACCOUNTING_BASE } from "./accountingNav";
import { formatCurrency, formatDateDMY } from "./LineGrid";
import BankReconcileImportPanel from "./BankReconcileImportPanel";

const TYPE_LABELS = { RECEIPT: "Receipt", PAYMENT: "Payment", CONTRA: "Contra", JOURNAL: "Journal", PURCHASE: "Purchase", CREDIT_NOTE: "Credit Note", DEBIT_NOTE: "Debit Note" };
const RECONCILABLE_TYPES = new Set(["RECEIPT", "PAYMENT", "CONTRA"]);
const STATUS_TABS = [["all", "All"], ["reconciled", "Reconciled"], ["unreconciled", "Unreconciled"]];

function rowKey(r) { return `${r.voucherId}::${r.voucherLineId}`; }

function BalanceValue({ value, overdraft }) {
  return (
    <div>
      <div className={`text-2xl font-extrabold ${overdraft ? "text-red-600" : "text-[#111827]"}`}>{formatCurrency(Math.abs(value))}</div>
      {overdraft && <div className="text-xs font-semibold uppercase tracking-[0.14em] text-red-600">Overdraft</div>}
    </div>
  );
}

/** Reconcile dialog - used for both a single row and a bulk selection; the
 * same Bank Cleared Date / Bank Reference fields either way (item 3). */
function ReconcileDialog({ open, count, defaultRef, busy, onCancel, onConfirm }) {
  const [clearedDate, setClearedDate] = useState("");
  const [reference, setReference] = useState("");
  useEffect(() => {
    if (open) {
      setClearedDate(new Date().toISOString().slice(0, 10));
      setReference(defaultRef || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{count > 1 ? `Reconcile ${count} transactions` : "Reconcile this transaction"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Bank Cleared Date *"><Input type="date" value={clearedDate} onChange={(e) => setClearedDate(e.target.value)} /></Field>
          <Field label="Bank Reference (optional)"><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Leave blank to keep the voucher's own reference" /></Field>
          <p className="text-xs text-[#6B7280]">This only marks the transaction as cleared by the bank - it does not change the accounting entry or Bank Book balance in any way.</p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>
          <Button type="button" disabled={busy || !clearedDate} onClick={() => onConfirm(clearedDate, reference)}>Reconcile</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BankReconciliationManualPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("all");
  const [voucherNo, setVoucherNo] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [party, setParty] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [reconcileTarget, setReconcileTarget] = useState(null); // { rows: [...] } | null

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/bank-accounts");
        const rows = Array.isArray(res.data) ? res.data : [];
        setAccounts(rows);
        if (rows.length === 1) setBankAccountId(rows[0].id);
      } catch { /* handled via empty state */ }
    })();
  }, []);

  const accountOptions = useMemo(() => accounts.map((a) => ({
    value: a.id, label: `${a.bankName} — ${a.accountName || ""} •••• ${a.accountNumber || ""}`.replace(/\s+/g, " ").trim(),
  })), [accounts]);

  const load = useCallback(async () => {
    if (!bankAccountId) { setData(null); return; }
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/bank-reconcile/transactions", {
        params: { bankAccountId, fromDate, toDate, status, voucherNo, referenceNo, party },
      });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load Bank Reconciliation"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [bankAccountId, fromDate, toDate, status, voucherNo, referenceNo, party]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const selectableRows = rows.filter((r) => RECONCILABLE_TYPES.has(r.voucherType));
  const selectedUnreconciled = rows.filter((r) => selected.has(rowKey(r)) && !r.reconciled);
  const selectedReconciled = rows.filter((r) => selected.has(rowKey(r)) && r.reconciled);

  const toggleRow = (r, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowKey(r)); else next.delete(rowKey(r));
      return next;
    });
  };
  const toggleAll = (checked) => {
    setSelected(checked ? new Set(selectableRows.map(rowKey)) : new Set());
  };

  const runReconcile = async (clearedDate, reference) => {
    if (!reconcileTarget) return;
    try {
      setBusy(true);
      const items = reconcileTarget.rows.map((r) => ({ voucherId: r.voucherId, voucherLineId: r.voucherLineId }));
      if (items.length === 1) {
        await api.post("/society/accounting/bank-reconcile/reconcile", {
          bankAccountId, voucherId: items[0].voucherId, voucherLineId: items[0].voucherLineId, clearedDate, referenceNo: reference,
        });
      } else {
        const res = await api.post("/society/accounting/bank-reconcile/bulk", { bankAccountId, action: "reconcile", items, clearedDate, referenceNo: reference });
        if (res.data?.failed) toast.error(`${res.data.failed} of ${items.length} could not be reconciled`);
      }
      toast.success(items.length > 1 ? `${items.length} transaction(s) reconciled` : "Transaction reconciled");
      setReconcileTarget(null);
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not reconcile"));
    } finally {
      setBusy(false);
    }
  };

  // PHASE 19 item 5 (Sensitive Action Confirmation): "Unreconcile
  // transaction" is explicitly named in the spec as requiring confirmation
  // with a clear statement of impact before proceeding - this page
  // previously called the unreconcile endpoint directly from the button's
  // onClick with no confirmation step at all. Kept as a plain window.confirm
  // (matching deleteDraft's pattern elsewhere in this module) rather than
  // the shared ReasonDialog, since the backend's unreconcile endpoints
  // (SocietyBankUnreconcileIn / bulk) take no reason field - the audit log
  // already captures the before/after bankReconciled state on every call via
  // _bank_reconcile_apply's existing _society_audit_log entry, so nothing
  // here needs a text reason to satisfy the audit trail requirement.
  const runUnreconcile = async (targetRows) => {
    if (!targetRows.length) return;
    const impact = targetRows.length === 1
      ? `Mark ${targetRows[0].voucherNumber || "this transaction"} as NOT cleared by the bank? Its Bank Cleared Date and reference will be removed. The accounting entry and Bank Book balance are unaffected.`
      : `Mark ${targetRows.length} transactions as NOT cleared by the bank? Their Bank Cleared Date and reference will be removed. The accounting entries and Bank Book balance are unaffected.`;
    if (!window.confirm(impact)) return;
    try {
      setBusy(true);
      const items = targetRows.map((r) => ({ voucherId: r.voucherId, voucherLineId: r.voucherLineId }));
      if (items.length === 1) {
        await api.post("/society/accounting/bank-reconcile/unreconcile", { bankAccountId, voucherId: items[0].voucherId, voucherLineId: items[0].voucherLineId });
      } else {
        const res = await api.post("/society/accounting/bank-reconcile/bulk", { bankAccountId, action: "unreconcile", items });
        if (res.data?.failed) toast.error(`${res.data.failed} of ${items.length} could not be unreconciled`);
      }
      toast.success(items.length > 1 ? `${items.length} transaction(s) unreconciled` : "Transaction unreconciled");
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not unreconcile"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Bank Reconciliation</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Mark each Receipt, Payment or Contra transaction as cleared by the bank. This never changes the accounting entry or the Bank Book balance - it only records when the bank actually cleared it.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4">
        <div className="w-80 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Bank Account</div>
          <SearchableSelect value={bankAccountId} onChange={setBankAccountId} options={accountOptions} placeholder="Select bank account" />
        </div>
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">From Date</div>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">To Date</div>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="flex gap-2">
          {STATUS_TABS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => setStatus(id)} className={`rounded-sm px-3 py-1.5 text-xs font-semibold ${status === id ? "bg-[#111827] text-white" : "bg-[#F3F4F6] text-[#374151]"}`}>{label}</button>
          ))}
        </div>
      </div>

      {!bankAccountId ? (
        <EmptyState>Select a Bank Account to begin reconciling.</EmptyState>
      ) : (
        <>
          {data && (
            <div className="grid grid-cols-2 gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4 sm:grid-cols-4">
              <DetailField label="Bank Account" value={data.bankAccountLabel} />
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Book Balance</div>
                <BalanceValue value={data.summary.bookBalance} overdraft={data.summary.bookBalanceOverdraft} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Unreconciled Deposits / Payments</div>
                <div className="mt-0.5 text-sm font-semibold text-[#111827]">{formatCurrency(data.summary.unreconciledDeposits)} / {formatCurrency(data.summary.unreconciledWithdrawals)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Reconciled / Bank Balance</div>
                <BalanceValue value={data.summary.reconciledBankBalance} overdraft={data.summary.reconciledBankBalanceOverdraft} />
              </div>
            </div>
          )}

          <BankReconcileImportPanel bankAccountId={bankAccountId} onReconciled={load} />

          <SectionCard
            title="Transactions"
            actions={
              <>
                {selectedUnreconciled.length > 0 && (
                  <Button size="sm" disabled={busy} onClick={() => setReconcileTarget({ rows: selectedUnreconciled })}>Reconcile Selected ({selectedUnreconciled.length})</Button>
                )}
                {selectedReconciled.length > 0 && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => runUnreconcile(selectedReconciled)}>Unreconcile Selected ({selectedReconciled.length})</Button>
                )}
              </>
            }
          >
            <div className="mb-3 grid gap-3 sm:grid-cols-3">
              <Input value={voucherNo} onChange={(e) => setVoucherNo(e.target.value)} placeholder="Search Voucher No." />
              <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="Search Reference / UTR / Cheque No." />
              <Input value={party} onChange={(e) => setParty(e.target.value)} placeholder="Search Party / Particulars" />
            </div>

            {loading ? (
              <div className="text-sm text-[#6B7280]">Loading...</div>
            ) : (
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"><Checkbox checked={selectableRows.length > 0 && selectableRows.every((r) => selected.has(rowKey(r)))} onCheckedChange={toggleAll} /></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Voucher No.</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Party / Particulars</TableHead>
                      <TableHead>Reference / UTR / Cheque No.</TableHead>
                      <TableHead className="text-right">Deposit</TableHead>
                      <TableHead className="text-right">Withdrawal</TableHead>
                      <TableHead>Cleared Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const canReconcile = RECONCILABLE_TYPES.has(r.voucherType);
                      return (
                        <TableRow key={rowKey(r)}>
                          <TableCell>
                            {canReconcile && <Checkbox checked={selected.has(rowKey(r))} onCheckedChange={(v) => toggleRow(r, v)} />}
                          </TableCell>
                          <TableCell>{formatDateDMY(r.voucherDate)}</TableCell>
                          <TableCell>
                            <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${r.voucherId}`)}>{r.voucherNumber}</button>
                          </TableCell>
                          <TableCell>{TYPE_LABELS[r.voucherType] || r.voucherType}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{r.partyName || r.narration || "-"}</TableCell>
                          <TableCell className="text-xs text-[#6B7280]">{r.referenceNo || "-"}</TableCell>
                          <TableCell className="text-right">{r.deposit ? formatCurrency(r.deposit) : "—"}</TableCell>
                          <TableCell className="text-right">{r.withdrawal ? formatCurrency(r.withdrawal) : "—"}</TableCell>
                          <TableCell className="text-xs">{r.clearedDate ? formatDateDMY(r.clearedDate) : "-"}</TableCell>
                          <TableCell>
                            {r.reconciled ? (
                              <span className="rounded-sm bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Reconciled</span>
                            ) : (
                              <span className="rounded-sm bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Unreconciled</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {!canReconcile ? (
                                <span className="text-xs text-[#9CA3AF]" title="Only Receipt, Payment and Contra entries can be reconciled here">Not applicable</span>
                              ) : r.reconciled ? (
                                <Button size="sm" variant="outline" disabled={busy} onClick={() => runUnreconcile([r])}>Unreconcile</Button>
                              ) : (
                                <Button size="sm" disabled={busy} onClick={() => setReconcileTarget({ rows: [r] })}>Reconcile</Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${r.voucherId}`)}>View</Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {rows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-sm text-[#6B7280]">No transactions in this range.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>
        </>
      )}

      <ReconcileDialog
        open={!!reconcileTarget}
        count={reconcileTarget?.rows?.length || 0}
        defaultRef={reconcileTarget?.rows?.length === 1 ? reconcileTarget.rows[0].referenceNo : ""}
        busy={busy}
        onCancel={() => setReconcileTarget(null)}
        onConfirm={runReconcile}
      />
    </div>
  );
}
